import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  MenuItem,
} from "@mui/material";
import PropTypes from "prop-types";

import { useTheme } from "@mui/material/styles";

import FirstPageIcon from "@mui/icons-material/FirstPage";

import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";

import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import LastPageIcon from "@mui/icons-material/LastPage";

import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

import FeatherIcon from "feather-icons-react";

import { toast } from "react-toastify";

import { useFormik } from "formik";

import * as Yup from "yup";

import {
  GetDashboardModulePagesList,
  AddDashboardModulePages as AddOrEditOrDeleteDashboardModulePages,
  UpdateDashboardModulePages,
  GetDashboardModulesList,
} from "../../../services/admin";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";

import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";
function TablePaginationActions(props) {
  const theme = useTheme();

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: 2.5,
      }}
    >
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>

      <IconButton onClick={handleBackButtonClick} disabled={page === 0}>
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>

      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const AddDashboardModulePages = () => {
  const [list, setList] = useState([]);

  const [modules, setModules] = useState([]);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [globalSearch, setGlobalSearch] = useState("");

  const [searchText, setSearchText] = useState({});

  const [activeFilter, setActiveFilter] = useState("");

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [order, setOrder] = useState("asc");

  const [orderBy, setOrderBy] = useState("");

  useEffect(() => {
    fetchModulePages();

    fetchModules();
  }, []);

  const fetchModulePages = async () => {
    try {
      const response = await GetDashboardModulePagesList();

      setList(response?.data || []);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load module pages");
    }
  };

  const fetchModules = async () => {
    try {
      const response = await GetDashboardModulesList();

      setModules(response?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const validationSchema = Yup.object({
    moduleID: Yup.number().required("Module is required"),

    pageName: Yup.string().trim().required("Page name is required"),

    uniqueCode: Yup.string()
      .trim()
      .required("Unique code is required")
      .test(
        "duplicate-unique-code",
        "Unique code already exists",
        function (value) {
          if (!value) return true;

          const isDuplicate = list.some(
            (item) =>
              item.uniqueCode?.toLowerCase().trim() ===
                value.toLowerCase().trim() && item.modulePageID !== editId,
          );

          return !isDuplicate;
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      moduleID: "",

      pageName: "",

      uniqueCode: "",

      isActive: true,
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          modulePageID: editId || 0,

          moduleID: values.moduleID,

          pageName: values.pageName?.trim(),

          uniqueCode: values.uniqueCode?.trim(),

          isActive: values.isActive,

          action: editId ? "UPDATE" : "INSERT",
        };

        if (editId) {
          await UpdateDashboardModulePages(payload);
        } else {
          await AddOrEditOrDeleteDashboardModulePages(payload);
        }

        toast.success(
          editId
            ? "Module page updated successfully"
            : "Module page added successfully",
        );

        fetchModulePages();

        resetForm();

        setEditId(null);
      } catch (error) {
        console.log(error);

        toast.error("Save failed");
      }
    },
  });

  const handleEdit = (item) => {
    setEditId(item.modulePageID);

    formik.setValues({
      moduleID: item.moduleID || "",

      pageName: item.pageName || "",

      uniqueCode: item.uniqueCode || "",

      isActive: item.isActive,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const resetForm = () => {
    setEditId(null);

    formik.resetForm();
  };

  const handleToggleStatus = async (item, checked) => {
    try {
      const payload = {
        modulePageID: item.modulePageID,

        moduleID: item.moduleID,

        pageName: item.pageName,

        uniqueCode: item.uniqueCode,

        isActive: checked,

        action: "UPDATE",
      };

      await UpdateDashboardModulePages(payload);

      toast.success(`Module Page ${checked ? "Activated" : "Deactivated"}`);

      fetchModulePages();
    } catch (error) {
      console.log(error);

      toast.error("Status update failed");
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);
  };

 const filteredList = list.filter((item) => {

  // GLOBAL SEARCH

  const globalMatch = Object.values(item)
    .join(" ")
    .toLowerCase()
    .includes(globalSearch.toLowerCase());

  // ACTIVE FILTER

  const activeMatch =
    activeFilter === ""
      ? true
      : activeFilter === "true"
      ? item.isActive === true ||
        item.isActive === 1
      : item.isActive === false ||
        item.isActive === 0;

  // COLUMN FILTERS

  const columnMatch = Object.keys(searchText).every((key) => {

    if (!searchText[key]) return true;

    let value = "";

    if (key === "moduleName") {

      value =
        modules
          .find(
            (x) =>
              x.moduleID === item.moduleID,
          )
          ?.moduleName?.toLowerCase() || "";

    } else {

      value =
        item[key]
          ?.toString()
          .toLowerCase() || "";
    }

    return value.includes(
      searchText[key].toLowerCase(),
    );
  });

  return (
    globalMatch &&
    activeMatch &&
    columnMatch
  );
});

  const sortedList = [...filteredList].sort((a, b) => {
    if (!orderBy) return 0;

    let valueA = a[orderBy];

    let valueB = b[orderBy];

    if (typeof valueA === "string") valueA = valueA.toLowerCase();

    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA < valueB) return order === "asc" ? -1 : 1;

    if (valueA > valueB) return order === "asc" ? 1 : -1;

    return 0;
  });

  const handleResetFilters = () => {
    setGlobalSearch("");

    setSearchText({});

    setShowAllFilters(false);

    setPage(0);
  };

  const renderHeader = (label, field) => (
    <TableCell>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? order : "asc"}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* FORM */}

      <Card
        sx={{
          mb: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="layers" width="18" />

          <Box sx={{ ml: 1 }}>
            {editId
              ? "Edit Dashboard Module Page"
              : "Add Dashboard Module Page"}
          </Box>
        </Box>

        <CardContent
          sx={{
            padding: "20px 30px",
          }}
        >
          <Grid container spacing={2}>
            {/* Module */}

            <Grid item lg={3} md={6} sm={12}>
              <CustomFormLabel>Module</CustomFormLabel>

              <TextField
                select
                fullWidth
                size="small"
                name="moduleID"
                value={formik.values.moduleID}
                onChange={formik.handleChange}
                error={
                  formik.touched.moduleID && Boolean(formik.errors.moduleID)
                }
                helperText={formik.touched.moduleID && formik.errors.moduleID}
              >
                {modules.map((item) => (
                  <MenuItem key={item.moduleID} value={item.moduleID}>
                    {item.moduleName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Page Name */}

            <Grid item lg={3} md={6} sm={12}>
              <CustomFormLabel>Page Name</CustomFormLabel>

              <CustomTextField
                fullWidth
                size="small"
                placeholder="Enter page name"
                name="pageName"
                value={formik.values.pageName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.pageName && Boolean(formik.errors.pageName)
                }
                helperText={formik.touched.pageName && formik.errors.pageName}
              />
            </Grid>

            {/* Unique Code */}

            <Grid item lg={3} md={6} sm={12}>
              <CustomFormLabel>Unique Code</CustomFormLabel>

              <CustomTextField
                fullWidth
                size="small"
                placeholder="Enter unique code"
                name="uniqueCode"
                value={formik.values.uniqueCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.uniqueCode && Boolean(formik.errors.uniqueCode)
                }
                helperText={
                  formik.touched.uniqueCode && formik.errors.uniqueCode
                }
              />
            </Grid>

            {/* Status */}

            <Grid item lg={1} md={6} sm={12}>
              <CustomFormLabel>Status</CustomFormLabel>

              <Box display="flex" alignItems="center" mt={1}>
                <Switch
                  checked={formik.values.isActive}
                  onChange={(e) =>
                    formik.setFieldValue("isActive", e.target.checked)
                  }
                />
              </Box>
            </Grid>

            {/* Buttons */}

            <Grid
              item
              lg={2}
              md={12}
              sm={12}
              display="flex"
              alignItems="center"
              mt={4}
            >
              <Button variant="contained" onClick={formik.handleSubmit}>
                {editId ? "Update" : "Add"}
              </Button>

              <Button sx={{ ml: 2 }} variant="outlined" onClick={resetForm}>
                {editId ? "Cancel" : "Reset"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TABLE */}

      <Card sx={{ p: 0 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          py={1.5}
          px={2}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          {/* TITLE */}

          <Box display="flex" alignItems="center">
            <FeatherIcon icon="database" width="18" />

            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              Existing Dashboard Module Pages ({filteredList.length})
            </Typography>
          </Box>

          {/* FILTERS */}

          <Box display="flex" alignItems="center" gap={1.5}>
            {/* GLOBAL SEARCH */}

            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);

                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),

                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setGlobalSearch("")}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* ACTIVE FILTER */}

            <TextField
              select
              size="small"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);

                setPage(0);
              }}
              sx={{
                minWidth: 140,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">All</option>

              <option value="true">Active</option>

              <option value="false">Inactive</option>
            </TextField>

            {/* FILTER ICON */}

            <Tooltip title="Column Filters">
              <FilterAltIcon
                sx={{
                  cursor: "pointer",
                  color: showAllFilters ? "primary.main" : "inherit",
                }}
                onClick={() => setShowAllFilters(!showAllFilters)}
              />
            </Tooltip>

            {/* RESET */}

            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <CardContent>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: showAllFilters ? 540 : 450,
              overflowY: filteredList.length > 5 ? "auto" : "visible",
              overflowX: "auto",
              scrollbarGutter: "stable",
            }}
          >
            <Table stickyHeader>
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <TableRow>
                  {renderHeader("ID", "modulePageID")}

                  {renderHeader("Module", "moduleName")}

                  {renderHeader("Page Name", "pageName")}

                  {renderHeader("Unique Code", "uniqueCode")}

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Action</TableCell>
                </TableRow>

                {/* COLUMN FILTERS */}

                {showAllFilters && (
                  <TableRow>
                    {[
                      "modulePageID",
                      "moduleName",
                      "pageName",
                      "uniqueCode",
                    ].map((field) => (
                      <TableCell key={field}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Search..."
                          value={searchText[field] || ""}
                          onChange={(e) => {
                            setSearchText((prev) => ({
                              ...prev,
                              [field]: e.target.value,
                            }));

                            setPage(0);
                          }}
                          InputProps={{
                            endAdornment: searchText[field] && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setSearchText((prev) => ({
                                      ...prev,
                                      [field]: "",
                                    }))
                                  }
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    ))}

                    <TableCell />

                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {filteredList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <>
                        <div>No data found</div>

                        <Button
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={handleResetFilters}
                        >
                          Clear Filters
                        </Button>
                      </>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? sortedList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage,
                    )
                  : sortedList
                ).map((row) => (
                  <TableRow key={row.modulePageID}>
                    <TableCell>{row.modulePageID}</TableCell>

                    <TableCell>
                      {modules.find((x) => x.moduleID === row.moduleID)
                        ?.moduleName || "-"}
                    </TableCell>

                    <TableCell>{row.pageName}</TableCell>

                    <TableCell>{row.uniqueCode}</TableCell>

                    <TableCell>
                      <Tooltip title={row.isActive ? "Active" : "Inactive"}>
                        <Switch
                          checked={row.isActive}
                          size="small"
                          onChange={(e) =>
                            handleToggleStatus(row, e.target.checked)
                          }
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[
                5,
                10,
                25,
                {
                  label: "All",
                  value: -1,
                },
              ]}
              component="div"
              count={filteredList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));

                setPage(0);
              }}
              ActionsComponent={TablePaginationActions}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AddDashboardModulePages;
